import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, type ConfirmDialogVariant } from "./ConfirmDialog";

export type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
};

export type UseConfirmResult = {
  /**
   * 確認モーダルを開いてユーザーの応答を待つ。
   * OK → ``true``、キャンセル / バックドロップ → ``false`` を返す。
   */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** コンポーネントの末尾に置いて描画するモーダル要素 */
  dialog: ReactElement;
};

/**
 * Promise ベースの確認モーダル hook。
 *
 * ``window.confirm()`` を置き換える共通実装。Provider 不要で各画面で個別に
 * 使える。返り値の ``dialog`` をコンポーネントの JSX 末尾に置いておけば、
 * ``confirm({ message: "..." })`` を await するだけでユーザー応答を取得できる。
 *
 * @example
 * const { confirm, dialog } = useConfirm();
 * const handleDelete = async () => {
 *   const ok = await confirm({
 *     title: "削除の確認",
 *     message: "この企業を削除します。",
 *     confirmText: "削除",
 *     variant: "destructive",
 *   });
 *   if (!ok) return;
 *   // ...削除処理
 * };
 * return (<><Button onClick={handleDelete}>削除</Button>{dialog}</>);
 *
 * @return {UseConfirmResult} confirm 関数と描画用 dialog 要素
 */
export function useConfirm(): UseConfirmResult {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ message: "" });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions): Promise<boolean> => {
    setOptions(next);
    setOpen(true);
    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const dialog = (
    <ConfirmDialog
      isOpen={open}
      title={options.title}
      message={options.message}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      variant={options.variant}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
}
